import { Box3, Mesh, Matrix4, Group } from "three";
import { OBB } from "assets/JS/OBB";
import { Robot } from "./Robot";
import { StaticMesh } from "./StaticMesh";
import { CollisionType, IRobotJoints } from "./enums";
import { builder } from "./global";

/**
 * Keeps collision meshes synchronised with their rendered transforms.
 * Important for GLB files exported from Cinema4D where meshes are nested.
 */
function updateCollisionTransform(mesh: Mesh) {
    mesh.updateWorldMatrix(true, false);

    const obb = mesh.userData.obb as OBB | undefined;

    if (!obb) return;

    const geometry = mesh.geometry;

    if (!geometry.boundingBox) {
        geometry.computeBoundingBox();
    }

    if (!geometry.boundingBox) return;

    const localOBB = new OBB().fromBox3(geometry.boundingBox);

    localOBB.applyMatrix4(mesh.matrixWorld);

    obb.copy(localOBB);
}

function updateCollisionGroup(group: Group) {
    group.updateWorldMatrix(true, true);

    group.traverse((child) => {
        if (child instanceof Mesh) {
            updateCollisionTransform(child);
        }
    });
}

/**
 * Wall collision
 */
export function checkCollisionWalls(obj1: StaticMesh | Robot): CollisionType {
    obj1.collisiongroup.updateWorldMatrix(true, true);

    const box = new Box3().setFromObject(obj1.collisiongroup);

    for (const wall of builder._walls) {
        wall.updateWorldMatrix(true, true);

        const wallBox = new Box3().setFromObject(wall);

        if (box.intersectsBox(wallBox)) {
            return CollisionType.Collision;
        }
    }

    return CollisionType.NoCollision;
}

/**
 * Standard collision checks
 */
export function checkCollision(obj1: StaticMesh | Robot, obj2: StaticMesh | Robot): CollisionType {
    updateCollisionGroup(obj1.group);
    updateCollisionGroup(obj2.group);

    let isCollision = false;

    if (obj1 instanceof StaticMesh) {
        if (obj2 instanceof StaticMesh) {
            const b1 = new Box3().setFromObject(obj1.collisiongroup);

            const b2 = new Box3().setFromObject(obj2.collisiongroup);

            if (!b1.intersectsBox(b2)) {
                return CollisionType.NoCollision;
            }

            for (const element of obj2.collisionEnvelop.obb) {
                if (checkBoxCollision(element, obj1.collisionEnvelop)) {
                    isCollision = true;
                    break;
                }
            }

            if (isCollision) {
                return CollisionType.Collision;
            }

            for (const element of obj2.collisionEnvelop.sphere) {
                if (checkSphereCollision(element, obj1.collisionEnvelop)) {
                    return CollisionType.Collision;
                }
            }
        } else {
            const b1 = new Box3().setFromObject(obj1.collisiongroup);

            const b2 = new Box3().setFromObject(obj2.collisiongroup);

            if (!b1.intersectsBox(b2)) {
                return CollisionType.NoCollision;
            }

            if (checkBoxCollision(obj2.base, obj1.collisionEnvelop)) {
                return CollisionType.Collision;
            }

            const armBox = new Box3().setFromObject(obj2.ArmGroup);

            if (b1.intersectsBox(armBox)) {
                for (const element of obj2.ArmDecomposition) {
                    if (checkSphereCollision(element, obj1.collisionEnvelop)) {
                        return CollisionType.Collision;
                    }
                }
            }
        }
    } else {
        if (obj2 instanceof StaticMesh) {
            return checkCollision(obj2, obj1);
        }

        if (obj2 instanceof Robot) {
            if (checkBoxBox(obj2.base, obj1.base)) {
                return CollisionType.Collision;
            }

            const arm2Box = new Box3().setFromObject(obj2.ArmGroup);

            const arm1Box = new Box3().setFromObject(obj1.ArmGroup);

            if (checkBoxBox3(obj1.base, arm2Box)) {
                for (const element of obj2.ArmDecomposition) {
                    if (checkBoxSphere(obj1.base, element)) {
                        isCollision = true;
                        break;
                    }
                }
            }

            if (isCollision) {
                return CollisionType.Collision;
            }

            if (arm1Box.intersectsBox(arm2Box)) {
                for (const element of obj2.ArmDecomposition) {
                    for (const element2 of obj1.ArmDecomposition) {
                        if (checkSphereSphere(element2, element)) {
                            return CollisionType.Collision;
                        }
                    }
                }
            }
        }
    }

    return CollisionType.NoCollision;
}

/**
 * OBB collision helpers
 */
function checkBoxCollision(
    box: Mesh,
    Collision: {
        obb: Mesh[];
        sphere: Mesh[];
    }
) {
    updateCollisionTransform(box);

    const obb = box.userData.obb as OBB;

    for (const obb1 of Collision.obb) {
        if (checkBoxBox(box, obb1)) {
            return true;
        }
    }

    for (const sph of Collision.sphere) {
        const bs = getBoundingSphere(sph);

        if (!bs) {
            continue;
        }

        if (obb.intersectsSphere(bs)) {
            return true;
        }
    }

    return false;
}

function checkSphereCollision(
    sphere: Mesh,
    Collision: {
        obb: Mesh[];
        sphere: Mesh[];
    }
) {
    const sph = getBoundingSphere(sphere);

    if (!sph) {
        return false;
    }

    for (const obb1 of Collision.obb) {
        updateCollisionTransform(obb1);

        if ((obb1.userData.obb as OBB).intersectsSphere(sph)) {
            return true;
        }
    }

    for (const sph1 of Collision.sphere) {
        const bs = getBoundingSphere(sph1);

        if (!bs) {
            continue;
        }

        if (sph.intersectsSphere(bs)) {
            return true;
        }
    }

    return false;
}

function checkBoxBox3(box1: Mesh, box2: Box3) {
    updateCollisionTransform(box1);

    return (box1.userData.obb as OBB).intersectsBox3(box2);
}

function checkBoxBox(box1: Mesh, box2: Mesh) {
    updateCollisionTransform(box1);
    updateCollisionTransform(box2);

    return (box1.userData.obb as OBB).intersectsOBB(box2.userData.obb as OBB);
}

function checkBoxSphere(box: Mesh, sphere: Mesh) {
    updateCollisionTransform(box);

    const obb = box.userData.obb as OBB;

    const sph = getBoundingSphere(sphere);

    if (!sph) {
        return false;
    }

    return obb.intersectsSphere(sph);
}

function checkSphereSphere(sphere1: Mesh, sphere2: Mesh) {
    const sph1 = getBoundingSphere(sphere1);

    if (!sph1) {
        return false;
    }

    const sph2 = getBoundingSphere(sphere2);

    if (!sph2) {
        return false;
    }

    return sph1.intersectsSphere(sph2);
}

/**
 * Gets world-space bounding sphere
 */
function getBoundingSphere(mesh: Mesh) {
    mesh.updateWorldMatrix(true, false);

    if (!mesh.geometry.boundingSphere) {
        mesh.geometry.computeBoundingSphere();
    }

    if (!mesh.geometry.boundingSphere) {
        return null;
    }

    const sphere = mesh.geometry.boundingSphere.clone();

    sphere.applyMatrix4(mesh.matrixWorld);

    return sphere;
}

/**
 * BVH Box collision
 */
function checkBoxBVHCollision(targetObject: Mesh | undefined, collider: Mesh | undefined): boolean {
    if (!targetObject || !collider) {
        return false;
    }

    targetObject.updateWorldMatrix(true, false);
    collider.updateWorldMatrix(true, false);

    if (!collider.geometry.boundingBox) {
        collider.geometry.computeBoundingBox();
    }

    if (!collider.geometry.boundingBox) {
        return false;
    }

    const transformMatrix = new Matrix4()
        .copy(targetObject.matrixWorld)
        .invert()
        .multiply(collider.matrixWorld);

    const bb = new Box3().copy(collider.geometry.boundingBox);

    const intersect = targetObject.geometry.boundsTree?.intersectsBox(bb, transformMatrix);

    return !!intersect;
}

/**
 * BVH Sphere collision
 */
function checkSphereBVHCollision(
    targetObject: Mesh | undefined,
    collider: Group,
    isSleepMode = false
): boolean {
    if (!targetObject) {
        return false;
    }

    targetObject.updateWorldMatrix(true, false);
    collider.updateWorldMatrix(true, true);

    for (let i = 0; i < collider.children.length; i++) {
        const child = collider.children[i];

        if (child.userData.joint === IRobotJoints.tip && !isSleepMode) {
            continue;
        }

        if (!(child instanceof Mesh)) {
            continue;
        }

        child.updateWorldMatrix(true, false);

        const transformMatrix = new Matrix4()
            .copy(targetObject.matrixWorld)
            .invert()
            .multiply(child.matrixWorld);

        if (!child.geometry.boundingSphere) {
            child.geometry.computeBoundingSphere();
        }

        if (!child.geometry.boundingSphere) {
            continue;
        }

        const sphere = child.geometry.boundingSphere.clone();

        sphere.applyMatrix4(transformMatrix);

        const intersect = targetObject.geometry.boundsTree?.intersectsSphere(sphere);

        if (intersect) {
            return true;
        }
    }

    return false;
}

/**
 * Finds Bed / Patient meshes inside GLB hierarchy
 */

function findBVHTarget(staticMesh: StaticMesh): Mesh[] {
    return staticMesh.meshes.filter((object): object is Mesh => {
        if (!(object instanceof Mesh)) {
            return false;
        }

        if (!object.geometry) {
            return false;
        }

        if (!object.geometry.boundsTree) {
            object.geometry.computeBoundsTree();
        }

        object.updateWorldMatrix(true, false);

        return true;
    });
}

/**
 * obj1 = collider
 * obj2 = target (patient / bed)
 */
export function checkBVHCollision(
    obj1: StaticMesh | Robot,
    obj2: StaticMesh | Robot
): CollisionType {
    obj1.group.updateWorldMatrix(true, true);
    obj2.group.updateWorldMatrix(true, true);

    if (obj1 instanceof StaticMesh) {
        if (obj2 instanceof StaticMesh) {
            const collider = obj1.collisionEnvelop.obb[0];

            const targetObject = findBVHTarget(obj2);

            if (targetObject.length === 0) {
                console.warn("BVH target mesh not found:", obj2.name);
                return CollisionType.NoCollision;
            }

            for (const mesh of targetObject) {
                if (checkBoxBVHCollision(mesh, collider)) {
                    return CollisionType.Collision;
                }
            }
        } else {
            const targetObject = findBVHTarget(obj1);

            if (targetObject.length === 0) {
                console.warn("BVH target mesh not found:", obj1.name);
                return CollisionType.NoCollision;
            }

            for (const mesh of targetObject) {
                if (checkBoxBVHCollision(mesh, obj2.base)) {
                    return CollisionType.Collision;
                }

                if (checkSphereBVHCollision(mesh, obj2.ArmGroup, obj2.isSleepMode)) {
                    return CollisionType.Collision;
                }
            }
        }
    } else {
        if (obj2 instanceof StaticMesh) {
            return checkBVHCollision(obj2, obj1);
        }
    }

    return CollisionType.NoCollision;
}
